import {
  ImageLoadObserver
} from "./chunk-YD36XMMR.js";
import {
  ImageUtils,
  widthAndHeightStylesAreBothSet
} from "./chunk-SEDOTBEN.js";
import "./chunk-SA55FI2N.js";
import {
  WidgetResize
} from "./chunk-ZFACQRIZ.js";
import "./chunk-QHREOC7B.js";
import "./chunk-ZPQVM7W7.js";
import "./chunk-NICE7ARI.js";
import "./chunk-U5S3T6FN.js";
import "./chunk-GJCFV3G2.js";
import {
  ButtonView,
  DropdownButtonView,
  Model,
  addListToDropdown,
  createDropdown
} from "./chunk-R4FN2FPU.js";
import "./chunk-J3DZJUY2.js";
import {
  CKEditorError,
  Collection,
  Command,
  Plugin,
  icons
} from "./chunk-H3OD3CNY.js";
import "./chunk-624QZG55.js";

// node_modules/@ckeditor/ckeditor5-image/src/imageresize/resizeimagecommand.js
var ResizeImageCommand = class extends Command {
  /**
   * @inheritDoc
   */
  refresh() {
    const editor = this.editor;
    const imageUtils = editor.plugins.get("ImageUtils");
    const element = imageUtils.getClosestSelectedImageElement(editor.model.document.selection);
    this.isEnabled = !!element;
    if (!element || !element.hasAttribute("resizedWidth")) {
      this.value = null;
    } else {
      this.value = {
        width: element.getAttribute("resizedWidth"),
        height: null
      };
    }
  }
  /**
   * Executes the command.
   *
   * ```ts
   * // Sets the width to 50%:
   * editor.execute( 'resizeImage', { width: '50%' } );
   *
   * // Removes the width attribute:
   * editor.execute( 'resizeImage', { width: null } );
   * ```
   *
   * @param options
   * @param options.width The new width of the image.
   * @fires execute
   */
  execute(options) {
    const editor = this.editor;
    const model = editor.model;
    const imageUtils = editor.plugins.get("ImageUtils");
    const imageElement = imageUtils.getClosestSelectedImageElement(model.document.selection);
    this.value = {
      width: options.width,
      height: null
    };
    if (imageElement) {
      model.change((writer) => {
        writer.setAttribute("resizedWidth", options.width, imageElement);
        writer.removeAttribute("resizedHeight", imageElement);
        imageUtils.setImageNaturalSizeAttributes(imageElement);
      });
    }
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/imageresize/imageresizeediting.js
var ImageResizeEditing = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ImageUtils];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImageResizeEditing";
  }
  /**
   * @inheritDoc
   */
  constructor(editor) {
    super(editor);
    editor.config.define("image", {
      resizeUnit: "%",
      resizeOptions: [
        {
          name: "resizeImage:original",
          value: null,
          icon: "original"
        },
        {
          name: "resizeImage:25",
          value: "25",
          icon: "small"
        },
        {
          name: "resizeImage:50",
          value: "50",
          icon: "medium"
        },
        {
          name: "resizeImage:75",
          value: "75",
          icon: "large"
        }
      ]
    });
  }
  /**
   * @inheritDoc
   */
  init() {
    const editor = this.editor;
    const resizeImageCommand = new ResizeImageCommand(editor);
    this._registerSchema();
    this._registerConverters("imageBlock");
    this._registerConverters("imageInline");
    editor.commands.add("resizeImage", resizeImageCommand);
    editor.commands.add("imageResize", resizeImageCommand);
  }
  _registerSchema() {
    if (this.editor.plugins.has("ImageBlockEditing")) {
      this.editor.model.schema.extend("imageBlock", { allowAttributes: ["resizedWidth", "resizedHeight"] });
    }
    if (this.editor.plugins.has("ImageInlineEditing")) {
      this.editor.model.schema.extend("imageInline", { allowAttributes: ["resizedWidth", "resizedHeight"] });
    }
  }
  /**
   * Registers image resize converters.
   *
   * @param imageType The type of the image.
   */
  _registerConverters(imageType) {
    const editor = this.editor;
    const imageUtils = editor.plugins.get("ImageUtils");
    editor.conversion.for("downcast").add((dispatcher) => dispatcher.on(`attribute:resizedWidth:${imageType}`, (evt, data, conversionApi) => {
      if (!conversionApi.consumable.consume(data.item, evt.name)) {
        return;
      }
      const viewWriter = conversionApi.writer;
      const viewImg = conversionApi.mapper.toViewElement(data.item);
      if (data.attributeNewValue !== null) {
        viewWriter.setStyle("width", data.attributeNewValue, viewImg);
        viewWriter.addClass("image_resized", viewImg);
      } else {
        viewWriter.removeStyle("width", viewImg);
        viewWriter.removeClass("image_resized", viewImg);
      }
    }));
    editor.conversion.for("dataDowncast").attributeToAttribute({
      model: {
        name: imageType,
        key: "resizedHeight"
      },
      view: (modelAttributeValue) => ({
        key: "style",
        value: {
          "height": modelAttributeValue
        }
      })
    });
    editor.conversion.for("editingDowncast").add((dispatcher) => dispatcher.on(`attribute:resizedHeight:${imageType}`, (evt, data, conversionApi) => {
      if (!conversionApi.consumable.consume(data.item, evt.name)) {
        return;
      }
      const viewWriter = conversionApi.writer;
      const viewImg = conversionApi.mapper.toViewElement(data.item);
      const target = imageType === "imageInline" ? imageUtils.findViewImgElement(viewImg) : viewImg;
      if (data.attributeNewValue !== null) {
        viewWriter.setStyle("height", data.attributeNewValue, target);
      } else {
        viewWriter.removeStyle("height", target);
      }
    }));
    editor.conversion.for("upcast").attributeToAttribute({
      view: {
        name: imageType === "imageBlock" ? "figure" : "img",
        styles: {
          width: /.+/
        }
      },
      model: {
        key: "resizedWidth",
        value: (viewElement) => {
          if (widthAndHeightStylesAreBothSet(viewElement)) {
            return null;
          }
          return viewElement.getStyle("width");
        }
      }
    });
    editor.conversion.for("upcast").attributeToAttribute({
      view: {
        name: imageType === "imageBlock" ? "figure" : "img",
        styles: {
          height: /.+/
        }
      },
      model: {
        key: "resizedHeight",
        value: (viewElement) => {
          if (widthAndHeightStylesAreBothSet(viewElement)) {
            return null;
          }
          return viewElement.getStyle("height");
        }
      }
    });
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/imageresize/imageresizebuttons.js
var RESIZE_ICONS = {
  small: icons.objectSizeSmall,
  medium: icons.objectSizeMedium,
  large: icons.objectSizeLarge,
  original: icons.objectSizeFull
};
var ImageResizeButtons = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ImageResizeEditing];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImageResizeButtons";
  }
  /**
   * @inheritDoc
   */
  constructor(editor) {
    super(editor);
    this._resizeUnit = editor.config.get("image.resizeUnit");
  }
  /**
   * @inheritDoc
   */
  init() {
    const editor = this.editor;
    const options = editor.config.get("image.resizeOptions");
    const command = editor.commands.get("resizeImage");
    this.bind("isEnabled").to(command);
    for (const option of options) {
      this._registerImageResizeButton(option);
    }
    this._registerImageResizeDropdown(options);
  }
  /**
   * A helper function that creates a standalone button component for the plugin.
   *
   * @param resizeOption A model of the resize option.
   */
  _registerImageResizeButton(option) {
    const editor = this.editor;
    const { name, value, icon } = option;
    const optionValueWithUnit = value ? value + this._resizeUnit : null;
    editor.ui.componentFactory.add(name, (locale) => {
      const button = new ButtonView(locale);
      const command = editor.commands.get("resizeImage");
      const labelText = this._getOptionLabelValue(option, true);
      if (!RESIZE_ICONS[icon]) {
        throw new CKEditorError("imageresizebuttons-missing-icon", editor, option);
      }
      button.set({
        // Use the `label` property for a verbose description (because of ARIA).
        label: labelText,
        icon: RESIZE_ICONS[icon],
        tooltip: labelText,
        isToggleable: true
      });
      button.bind("isEnabled").to(this);
      button.bind("isOn").to(command, "value", getIsOnButtonCallback(optionValueWithUnit));
      this.listenTo(button, "execute", () => {
        editor.execute("resizeImage", { width: optionValueWithUnit });
      });
      return button;
    });
  }
  /**
   * A helper function that creates a dropdown component for the plugin containing all the resize options defined in
   * the editor configuration.
   *
   * @param options An array of configured options.
   */
  _registerImageResizeDropdown(options) {
    const editor = this.editor;
    const t = editor.t;
    const originalSizeOption = options.find((option) => !option.value);
    const componentCreator = (locale) => {
      const command = editor.commands.get("resizeImage");
      const dropdownView = createDropdown(locale, DropdownButtonView);
      const dropdownButton = dropdownView.buttonView;
      const accessibleLabel = t("Resize image");
      dropdownButton.set({
        tooltip: accessibleLabel,
        commandValue: originalSizeOption.value,
        icon: RESIZE_ICONS.medium,
        isToggleable: true,
        label: this._getOptionLabelValue(originalSizeOption),
        withText: true,
        class: "ck-resize-image-button",
        ariaLabel: accessibleLabel,
        ariaLabelledBy: void 0
      });
      dropdownButton.bind("label").to(command, "value", (commandValue) => {
        if (commandValue && commandValue.width) {
          return commandValue.width;
        } else {
          return this._getOptionLabelValue(originalSizeOption);
        }
      });
      dropdownView.bind("isEnabled").to(this);
      addListToDropdown(dropdownView, () => this._getResizeDropdownListItemDefinitions(options, command), {
        ariaLabel: t("Image resize list"),
        role: "menu"
      });
      this.listenTo(dropdownView, "execute", (evt) => {
        editor.execute(evt.source.commandName, { width: evt.source.commandValue });
        editor.editing.view.focus();
      });
      return dropdownView;
    };
    editor.ui.componentFactory.add("resizeImage", componentCreator);
    editor.ui.componentFactory.add("imageResize", componentCreator);
  }
  /**
   * A helper function for creating an option label value string.
   *
   * @param option A resize option object.
   * @param forTooltip An optional flag for creating a tooltip label.
   * @returns A user-defined label combined from the numeric value and the resize unit or the default label
   * for reset options (`Original`).
   */
  _getOptionLabelValue(option, forTooltip = false) {
    const t = this.editor.t;
    if (option.label) {
      return option.label;
    } else if (forTooltip) {
      if (option.value) {
        return t("Resize image to %0", option.value + this._resizeUnit);
      } else {
        return t("Resize image to the original size");
      }
    } else {
      if (option.value) {
        return option.value + this._resizeUnit;
      } else {
        return t("Original");
      }
    }
  }
  /**
   * A helper function that parses the resize options and returns list item definitions ready for use in the dropdown.
   *
   * @param options The resize options.
   * @param command The resize image command.
   * @returns Dropdown item definitions.
   */
  _getResizeDropdownListItemDefinitions(options, command) {
    const itemDefinitions = new Collection();
    options.map((option) => {
      const optionValueWithUnit = option.value ? option.value + this._resizeUnit : null;
      const definition = {
        type: "button",
        model: new Model({
          commandName: "resizeImage",
          commandValue: optionValueWithUnit,
          label: this._getOptionLabelValue(option),
          role: "menuitemradio",
          withText: true,
          icon: null
        })
      };
      definition.model.bind("isOn").to(command, "value", getIsOnButtonCallback(optionValueWithUnit));
      itemDefinitions.add(definition);
    });
    return itemDefinitions;
  }
};
function getIsOnButtonCallback(value) {
  return (commandValue) => {
    const objectCommandValue = commandValue;
    if (value === null && objectCommandValue === value) {
      return true;
    }
    return objectCommandValue !== null && objectCommandValue.width === value;
  };
}

// node_modules/@ckeditor/ckeditor5-image/src/imageresize/imageresizehandles.js
var RESIZABLE_IMAGES_CSS_SELECTOR = "figure.image.ck-widget > img,figure.image.ck-widget > picture > img,figure.image.ck-widget > a > img,figure.image.ck-widget > a > picture > img,span.image-inline.ck-widget > img,span.image-inline.ck-widget > picture > img";
var RESIZED_IMAGE_CLASS = "image_resized";
var ImageResizeHandles = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [WidgetResize, ImageUtils];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImageResizeHandles";
  }
  /**
   * @inheritDoc
   */
  init() {
    const command = this.editor.commands.get("resizeImage");
    this.bind("isEnabled").to(command);
    this._setupResizerCreator();
  }
  /**
   * Attaches the listeners responsible for creating a resizer for each image, except for images inside the HTML embed preview.
   */
  _setupResizerCreator() {
    const editor = this.editor;
    const editingView = editor.editing.view;
    const imageUtils = editor.plugins.get("ImageUtils");
    editingView.addObserver(ImageLoadObserver);
    this.listenTo(editingView.document, "imageLoaded", (evt, domEvent) => {
      if (!domEvent.target.matches(RESIZABLE_IMAGES_CSS_SELECTOR)) {
        return;
      }
      const domConverter = editor.editing.view.domConverter;
      const imageView = domConverter.domToView(domEvent.target);
      const widgetView = imageUtils.getImageWidgetFromImageView(imageView);
      let resizer = this.editor.plugins.get(WidgetResize).getResizerByViewElement(widgetView);
      if (resizer) {
        resizer.redraw();
        return;
      }
      const mapper = editor.editing.mapper;
      const imageModel = mapper.toModelElement(widgetView);
      resizer = editor.plugins.get(WidgetResize).attachTo({
        unit: editor.config.get("image.resizeUnit"),
        modelElement: imageModel,
        viewElement: widgetView,
        editor,
        getHandleHost(domWidgetElement) {
          return domWidgetElement.querySelector("img");
        },
        getResizeHost() {
          return domConverter.mapViewToDom(mapper.toViewElement(imageModel.parent));
        },
        // TODO consider other positions.
        isCentered() {
          const imageStyle = imageModel.getAttribute("imageStyle");
          return !imageStyle || imageStyle == "block" || imageStyle == "alignCenter";
        },
        onCommit(newValue) {
          editingView.change((writer) => {
            writer.removeClass(RESIZED_IMAGE_CLASS, widgetView);
          });
          editor.execute("resizeImage", { width: newValue });
        }
      });
      resizer.on("updateSize", () => {
        if (!widgetView.hasClass(RESIZED_IMAGE_CLASS)) {
          editingView.change((writer) => {
            writer.addClass(RESIZED_IMAGE_CLASS, widgetView);
          });
        }
        const target = imageModel.name === "imageInline" ? imageView : widgetView;
        if (target.getStyle("height")) {
          editingView.change((writer) => {
            writer.removeStyle("height", target);
          });
        }
      });
      resizer.bind("isEnabled").to(this);
    });
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/imageresize.js
import "/tmp/build_5d4c22f1/node_modules/@ckeditor/ckeditor5-image/theme/imageresize.css";
var ImageResize = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ImageResizeEditing, ImageResizeHandles, ImageResizeButtons];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImageResize";
  }
};
export {
  ImageResize as default
};
/*! Bundled license information:

@ckeditor/ckeditor5-image/src/imageresize/resizeimagecommand.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/imageresize/imageresizeediting.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/imageresize/imageresizebuttons.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/imageresize/imageresizehandles.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/imageresize.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)
*/
//# sourceMappingURL=@ckeditor_ckeditor5-image_src_imageresize.js.map
